import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import Dica from '@/components/feature/Dica';
import Modal from '@/components/ui/Modal';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarCentavos } from '@/pages/loja/utils';
import { horaBrasilia, normalizarTurno, normalizarTurnosEvento, parseDinheiroCentavos } from '../turno';
import type { CaixaFechamento, CaixaTurno, CaixaTurnoEvento } from '../types';

type Estado = 'carregando' | 'pronto' | 'sem-acesso' | 'erro';

interface InfoEvento {
  nome: string;
  direcao: boolean;
}

// Fechamento do caixa: totais por meio, por operador, por produto e conferência
// do dinheiro (só na tela). Direção pode filtrar por período.
export default function FechamentoCaixaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventoIdParam = Number(searchParams.get('evento'));
  const { user, pronto } = useAuth();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [info, setInfo] = useState<InfoEvento | null>(null);
  const [fechamento, setFechamento] = useState<CaixaFechamento | null>(null);

  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [dinheiroContado, setDinheiroContado] = useState('');

  // Turno de caixa (abrir/fechar). Se a chamada falhar, nada é mostrado.
  const [turno, setTurno] = useState<CaixaTurno | null>(null);
  const [fechado, setFechado] = useState<CaixaTurno | null>(null);
  const [turnosEvento, setTurnosEvento] = useState<CaixaTurnoEvento[]>([]);
  const [contadoTurno, setContadoTurno] = useState('');
  const [observacao, setObservacao] = useState('');
  const [confirmandoFechar, setConfirmandoFechar] = useState(false);
  const [fechando, setFechando] = useState(false);
  const [erroFechar, setErroFechar] = useState<string | null>(null);
  const [avisoContado, setAvisoContado] = useState(false);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        let id = Number.isFinite(eventoIdParam) && eventoIdParam > 0 ? eventoIdParam : null;
        if (id == null) {
          const salvo = Number(localStorage.getItem('caixa:evento'));
          if (Number.isFinite(salvo) && salvo > 0) id = salvo;
        }

        if (id == null) {
          // descobre o primeiro evento onde opera caixa
          const { data: meusIds } = await supabase.rpc('meus_eventos');
          const ids = new Set<number>(((meusIds as number[]) || []));
          const { data: pontos } = await supabase.rpc('loja_meus_pontos');
          ((pontos as { evento_id: number }[]) || []).forEach((p) => {
            if (p.evento_id != null) ids.add(p.evento_id);
          });
          for (const cand of ids) {
            const { data, error } = await supabase.rpc('caixa_produtos', { p_evento: cand });
            if (!error && data) {
              id = cand;
              break;
            }
          }
        }

        if (id == null) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        if (ativo) setEventoId(id);

        const { data: produtos, error: errProd } = await supabase.rpc('caixa_produtos', {
          p_evento: id,
        });
        if (errProd) throw errProd;
        const p = produtos as { evento?: { nome?: string }; direcao?: boolean };
        if (ativo) setInfo({ nome: p.evento?.nome ?? 'Evento', direcao: !!p.direcao });

        await carregar(id);
        void carregarTurno(id);
        if (p.direcao) void carregarTurnosEvento(id);
        if (ativo) setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, eventoIdParam, pronto, user]);

  async function carregar(id: number, desdeStr?: string, ateStr?: string) {
    const params: Record<string, unknown> = { p_evento: id };
    if (desdeStr) params.p_desde = new Date(`${desdeStr}T00:00:00`).toISOString();
    if (ateStr) params.p_ate = new Date(`${ateStr}T23:59:59`).toISOString();
    const { data, error } = await supabase.rpc('caixa_fechamento', params);
    if (error) throw error;
    setFechamento((data as CaixaFechamento) ?? null);
  }

  // Lê o turno aberto (se houver). Qualquer erro esconde o bloco novo.
  async function carregarTurno(id: number) {
    try {
      const { data, error } = await supabase.rpc('caixa_turno_atual', { p_evento: id });
      if (error) throw error;
      setTurno(normalizarTurno(data));
    } catch {
      setTurno(null);
    }
  }

  // Lista os turnos do evento (só a direção). Erro → lista vazia (seção some).
  async function carregarTurnosEvento(id: number) {
    try {
      const { data, error } = await supabase.rpc('caixa_turnos_do_evento', { p_evento: id });
      if (error) throw error;
      setTurnosEvento(normalizarTurnosEvento(data));
    } catch {
      setTurnosEvento([]);
    }
  }

  async function fecharCaixa() {
    if (!turno) return;
    const alvo = turno;
    const contadoCentavos = parseDinheiroCentavos(contadoTurno) ?? 0;
    setFechando(true);
    setErroFechar(null);
    try {
      const { data, error } = await supabase.rpc('caixa_turno_fechar', {
        p_turno: alvo.turno_id,
        p_contado_centavos: contadoCentavos,
        p_observacao: observacao.trim() || null,
      });
      if (error) throw error;

      const t =
        normalizarTurno(data) ?? {
          ...alvo,
          fechado_em: new Date().toISOString(),
          contado_centavos: contadoCentavos,
          diferenca_centavos: contadoCentavos - alvo.esperado_centavos,
        };
      setFechado(t);
      setTurno(null);
      setContadoTurno('');
      setObservacao('');
      setConfirmandoFechar(false);
      if (eventoId != null) {
        void carregar(eventoId);
        void carregarTurnosEvento(eventoId);
      }
    } catch (e) {
      setErroFechar(mensagemAmigavel(e));
      setConfirmandoFechar(false);
    } finally {
      setFechando(false);
    }
  }

  async function aplicarFiltro() {
    if (eventoId == null) return;
    setEstado('carregando');
    try {
      await carregar(eventoId, desde || undefined, ate || undefined);
      setEstado('pronto');
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setEstado('erro');
    }
  }

  function dataLocalISO(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dia}`;
  }

  function filtrarHoje() {
    if (eventoId == null) return;
    const hoje = dataLocalISO(new Date());
    setDesde(hoje);
    setAte(hoje);
    setEstado('carregando');
    carregar(eventoId, hoje, hoje)
      .then(() => setEstado('pronto'))
      .catch((e) => {
        setErro(mensagemAmigavel(e));
        setEstado('erro');
      });
  }

  const maxMetodo = useMemo(() => {
    if (!fechamento) return 1;
    return Math.max(
      1,
      fechamento.por_metodo.dinheiro?.total_centavos ?? 0,
      fechamento.por_metodo.pix?.total_centavos ?? 0,
      fechamento.por_metodo.cartao?.total_centavos ?? 0,
    );
  }, [fechamento]);

  const diferencaDinheiro = useMemo(() => {
    const contado = Number(dinheiroContado.replace(',', '.'));
    if (!Number.isFinite(contado) || dinheiroContado.trim() === '' || !fechamento) return null;
    const contadoCentavos = Math.round(contado * 100);
    return contadoCentavos - (fechamento.por_metodo.dinheiro?.total_centavos ?? 0);
  }, [dinheiroContado, fechamento]);

  // Diferença ao vivo do turno (contado − esperado).
  const contadoTurnoCentavos = parseDinheiroCentavos(contadoTurno);
  const esperadoTurno = turno?.esperado_centavos ?? 0;
  const diferencaTurno =
    contadoTurnoCentavos == null ? null : contadoTurnoCentavos - esperadoTurno;

  function formatarHora(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Cor da diferença: 0 verde, negativa vermelha, positiva âmbar.
  function corDiferenca(v: number | null): string {
    if (v == null) return 'text-foreground-950';
    if (v === 0) return 'text-success-600';
    if (v < 0) return 'text-danger-600';
    return 'text-amber-600';
  }

  function textoDiferenca(v: number | null): string {
    if (v == null) return '—';
    if (v === 0) return 'Bateu! ✓';
    if (v < 0) return `Faltou ${formatarCentavos(Math.abs(v))}`;
    return `Sobrou ${formatarCentavos(v)}`;
  }

  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro' || estado === 'sem-acesso') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">
            {estado === 'erro' ? 'Não foi possível carregar' : 'Sem acesso'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {estado === 'erro' ? erro : 'Você não opera o caixa deste evento.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/caixa')}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Voltar ao caixa
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100 print:bg-white">
      <div className="mx-auto w-full max-w-[960px] px-4 py-6">
        {/* Topo */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate('/caixa')}
              className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900 print:hidden"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Voltar ao caixa
            </button>
            <div className="mt-2 flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground-950">Fechamento do caixa</h1>
              <Dica id="caixa.fechamento" />
            </div>
            <p className="text-sm text-secondary-500">{info?.nome ?? ''}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            {info?.direcao && eventoId != null && (
              <button
                type="button"
                onClick={() => navigate(`/gerenciar/${eventoId}/relatorio-fechamento`)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-file-chart-line" />
                </span>
                Relatório do fechamento (PDF)
              </button>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <span className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-printer-line" />
                </span>
                Imprimir fechamento
              </span>
            </button>
          </div>
        </div>

        {/* Seu caixa (turno aberto) */}
        {turno && (
          <section className="mt-5 rounded-2xl border-2 border-primary-200 bg-white p-5 print:hidden">
            <h2 className="text-base font-semibold text-foreground-950">Seu caixa</h2>
            <p className="mt-1 text-sm text-secondary-500">
              Aberto às {horaBrasilia(turno.aberto_em)}
              {turno.caixa ? ` · ${turno.caixa}` : ''}
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
              <div className="rounded-xl bg-background-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">
                  Troco inicial
                </p>
                <p className="mt-1 text-xl font-bold tabular-nums text-foreground-950">
                  {formatarCentavos(turno.troco_inicial_centavos)}
                </p>
              </div>
              <div className="rounded-xl bg-background-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">
                  Vendas em dinheiro
                </p>
                <p className="mt-1 text-xl font-bold tabular-nums text-foreground-950">
                  {formatarCentavos(turno.resumo?.dinheiro_centavos ?? 0)}
                </p>
                <p className="text-xs text-secondary-500">
                  {turno.resumo?.dinheiro_vendas ?? 0} vendas
                </p>
              </div>
              <div className="rounded-xl bg-background-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">
                  Sangrias
                </p>
                <p className="mt-1 text-xl font-bold tabular-nums text-danger-600">
                  − {formatarCentavos(turno.sangria_centavos)}
                </p>
              </div>
              <div className="rounded-xl border-2 border-primary-200 bg-primary-50 p-3">
                <p className="text-xs font-bold uppercase tracking-wide text-primary-700">
                  Esperado na gaveta
                </p>
                <p className="mt-1 text-2xl font-extrabold tabular-nums text-foreground-950">
                  {formatarCentavos(turno.esperado_centavos)}
                </p>
              </div>
            </div>

            <p className="mt-2 text-xs text-secondary-500">
              Pix {formatarCentavos(turno.resumo?.pix_centavos ?? 0)} · Cartão{' '}
              {formatarCentavos(turno.resumo?.cartao_centavos ?? 0)} · Desfeitas{' '}
              {turno.resumo?.desfeitas ?? 0}
            </p>

            {turno.sangrias.length > 0 && (
              <div className="mt-3 rounded-xl bg-background-50 p-3">
                <p className="text-sm font-medium text-foreground-800">
                  Sangrias − {formatarCentavos(turno.sangria_centavos)}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {turno.sangrias.map((s, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-2 text-[13px] text-secondary-600 tabular-nums"
                    >
                      <span>{horaBrasilia(s.em)}</span>
                      <span>
                        − {formatarCentavos(s.valor_centavos)}
                        {s.observacao ? ` · ${s.observacao}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-foreground-900">
                Dinheiro contado na gaveta
              </label>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="text"
                  inputMode="decimal"
                  value={contadoTurno}
                  onChange={(e) => {
                    setContadoTurno(e.target.value.replace(/[^0-9,.]/g, ''));
                    setAvisoContado(false);
                  }}
                  placeholder="0,00"
                  className="w-full rounded-xl border-2 border-secondary-300 bg-white px-4 py-2.5 text-lg font-bold tabular-nums text-foreground-950 outline-none focus:border-primary-500 sm:w-48"
                />
                {diferencaTurno != null && (
                  <span className={`text-base font-bold ${corDiferenca(diferencaTurno)}`}>
                    {textoDiferenca(diferencaTurno)}
                  </span>
                )}
              </div>
              {avisoContado && (
                <p className="mt-1 text-sm text-danger-600">
                  Digite quanto dinheiro tem na gaveta (pode ser 0).
                </p>
              )}
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-foreground-900">
                Observação (opcional)
              </label>
              <input
                type="text"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-secondary-300 bg-white px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-500"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                if (contadoTurno.trim() === '') {
                  setAvisoContado(true);
                  return;
                }
                setConfirmandoFechar(true);
              }}
              className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:w-auto"
            >
              Fechar caixa e salvar
            </button>
          </section>
        )}

        {/* Caixa fechado (recibo na tela) */}
        {fechado && (
          <section className="mt-5 rounded-2xl border border-secondary-200 bg-white p-5">
            <h2 className="text-base font-semibold text-foreground-950">
              Caixa fechado às {horaBrasilia(fechado.fechado_em)}
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Recibo rotulo="Troco" valor={formatarCentavos(fechado.troco_inicial_centavos)} />
              <Recibo rotulo="Esperado" valor={formatarCentavos(fechado.esperado_centavos)} />
              <Recibo
                rotulo="Contado"
                valor={formatarCentavos(fechado.contado_centavos ?? 0)}
              />
              <Recibo
                rotulo="Diferença"
                valor={textoDiferenca(fechado.diferenca_centavos)}
                cor={corDiferenca(fechado.diferenca_centavos)}
              />
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => window.print()}
                className="cursor-pointer whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-printer-line" />
                  </span>
                  Imprimir
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/caixa')}
                className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Abrir novo caixa
              </button>
            </div>
          </section>
        )}

        {fechamento && (
          <>
            {/* Tiles */}
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile rotulo="Total" valor={formatarCentavos(fechamento.total_centavos)} />
              <Tile rotulo="Vendas" valor={String(fechamento.vendas)} />
              <Tile rotulo="Fichas" valor={String(fechamento.fichas)} />
              <Tile rotulo="Desfeitas" valor={String(fechamento.desfeitas)} />
            </div>

            <p className="mt-3 text-xs text-secondary-500">
              {formatarHora(fechamento.primeira_em)} até {formatarHora(fechamento.ultima_em)}
            </p>

            {/* Por meio */}
            <section className="mt-5 rounded-2xl border border-secondary-200 bg-white p-5">
              <h2 className="text-base font-semibold text-foreground-950">Por meio</h2>
              <div className="mt-4 space-y-3">
                <BarraMetodo
                  rotulo="Dinheiro"
                  vendas={fechamento.por_metodo.dinheiro?.vendas ?? 0}
                  total={fechamento.por_metodo.dinheiro?.total_centavos ?? 0}
                  max={maxMetodo}
                  cor="bg-success-500"
                />
                <BarraMetodo
                  rotulo="Pix"
                  vendas={fechamento.por_metodo.pix?.vendas ?? 0}
                  total={fechamento.por_metodo.pix?.total_centavos ?? 0}
                  max={maxMetodo}
                  cor="bg-accent-500"
                />
                <BarraMetodo
                  rotulo="Cartão (maquininha)"
                  vendas={fechamento.por_metodo.cartao?.vendas ?? 0}
                  total={fechamento.por_metodo.cartao?.total_centavos ?? 0}
                  max={maxMetodo}
                  cor="bg-secondary-500"
                />
              </div>

              {/* Conferência do dinheiro (v1) — escondida enquanto há turno/recibo. */}
              {!turno && !fechado && (
                <div className="mt-5 border-t border-secondary-100 pt-4">
                  <label className="block text-sm font-medium text-foreground-900">
                    Dinheiro contado na gaveta
                  </label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={dinheiroContado}
                      onChange={(e) => setDinheiroContado(e.target.value.replace(/[^0-9,.]/g, ''))}
                      placeholder="0,00"
                      className="w-full rounded-xl border-2 border-secondary-300 bg-white px-4 py-2.5 text-lg font-bold tabular-nums text-foreground-950 outline-none focus:border-primary-500 sm:w-48"
                    />
                    {diferencaDinheiro != null && (
                      <span
                        className={`text-base font-bold ${
                          diferencaDinheiro === 0
                            ? 'text-success-600'
                            : diferencaDinheiro > 0
                              ? 'text-success-600'
                              : 'text-danger-600'
                        }`}
                      >
                        {diferencaDinheiro === 0
                          ? 'Bateu! ✓'
                          : `${diferencaDinheiro > 0 ? '+' : ''}${formatarCentavos(diferencaDinheiro)}`}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-secondary-500">Só confere — não grava nada (v1).</p>
                </div>
              )}
            </section>

            {/* Caixas do evento (direção) */}
            {info?.direcao && turnosEvento.length > 0 && (
              <section className="mt-5 rounded-2xl border border-secondary-200 bg-white p-5">
                <h2 className="text-base font-semibold text-foreground-950">Caixas do evento</h2>
                <ul className="mt-3 divide-y divide-secondary-100">
                  {turnosEvento.map((t, i) => (
                    <li key={i} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-medium text-foreground-900">
                          {t.operador}
                          {t.caixa ? <span className="text-secondary-500"> · {t.caixa}</span> : null}
                        </p>
                        <p className="text-xs text-secondary-500 tabular-nums">
                          {horaBrasilia(t.aberto_em)}–{t.fechado_em ? horaBrasilia(t.fechado_em) : 'aberto'}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm tabular-nums">
                        <span className="text-secondary-500">
                          Troco {formatarCentavos(t.troco_inicial_centavos)}
                        </span>
                        {t.sangria_centavos > 0 && (
                          <span className="text-danger-600">
                            Sangrias − {formatarCentavos(t.sangria_centavos)}
                          </span>
                        )}
                        <span className="font-bold text-foreground-950">
                          {formatarCentavos(t.esperado_centavos)}
                        </span>
                        <span className="text-secondary-500">
                          Contado {t.contado_centavos == null ? '—' : formatarCentavos(t.contado_centavos)}
                        </span>
                        <span className={`font-bold ${corDiferenca(t.diferenca_centavos)}`}>
                          {textoDiferenca(t.diferenca_centavos)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Filtro de período (direção) */}
            {info?.direcao && (
              <section className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-secondary-200 bg-white p-5 print:hidden">
                <div>
                  <label className="block text-sm font-medium text-foreground-900">Desde</label>
                  <input
                    type="date"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                    className="mt-1 rounded-xl border-2 border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-900">Até</label>
                  <input
                    type="date"
                    value={ate}
                    onChange={(e) => setAte(e.target.value)}
                    className="mt-1 rounded-xl border-2 border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={filtrarHoje}
                  className="cursor-pointer whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  Hoje
                </button>
                <button
                  type="button"
                  onClick={aplicarFiltro}
                  className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Filtrar
                </button>
              </section>
            )}

            {/* Por operador */}
            {fechamento.por_operador.length > 0 && (
              <section className="mt-5 rounded-2xl border border-secondary-200 bg-white p-5">
                <h2 className="text-base font-semibold text-foreground-950">Por operador</h2>
                <ul className="mt-3 divide-y divide-secondary-100">
                  {fechamento.por_operador.map((o) => (
                    <li key={o.operador} className="flex items-center justify-between gap-3 py-2.5">
                      <span className="min-w-0 text-[15px] text-foreground-800">
                        {o.operador}{' '}
                        <span className="text-secondary-500">· {o.vendas} vendas</span>
                      </span>
                      <span className="shrink-0 font-bold text-foreground-950">
                        {formatarCentavos(o.total_centavos)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Por produto */}
            {fechamento.por_produto.length > 0 && (
              <section className="mt-5 rounded-2xl border border-secondary-200 bg-white p-5">
                <h2 className="text-base font-semibold text-foreground-950">Por produto</h2>
                <ul className="mt-3 divide-y divide-secondary-100">
                  {fechamento.por_produto.map((p, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-2.5">
                      <span className="min-w-0 text-[15px] text-foreground-800">
                        {p.produto}{' '}
                        <span className="text-secondary-500">
                          · {p.ponto} · {p.fichas} {p.fichas === 1 ? 'ficha' : 'fichas'}
                        </span>
                      </span>
                      <span className="shrink-0 font-bold text-foreground-950">
                        {formatarCentavos(p.total_centavos)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>

      {/* Confirmação de fechamento */}
      <Modal
        aberto={confirmandoFechar}
        titulo="Fechar o caixa?"
        aoFechar={() => setConfirmandoFechar(false)}
      >
        <p className="text-sm text-foreground-700">
          Depois de fechar não dá para mudar. Fechar o caixa?
        </p>
        {erroFechar && <p className="mt-2 text-sm text-danger-600">{erroFechar}</p>}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setConfirmandoFechar(false)}
            disabled={fechando}
            className="cursor-pointer whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-60"
          >
            Ainda não
          </button>
          <button
            type="button"
            onClick={() => void fecharCaixa()}
            disabled={fechando}
            className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {fechando ? 'Fechando…' : 'Fechar caixa'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function Tile({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-2xl border border-secondary-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{rotulo}</p>
      <p className="mt-1 text-3xl font-extrabold tabular-nums text-foreground-950">{valor}</p>
    </div>
  );
}

function Recibo({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div className="rounded-xl bg-background-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{rotulo}</p>
      <p className={`mt-1 text-lg font-bold tabular-nums ${cor ?? 'text-foreground-950'}`}>
        {valor}
      </p>
    </div>
  );
}

function BarraMetodo({
  rotulo,
  vendas,
  total,
  max,
  cor,
}: {
  rotulo: string;
  vendas: number;
  total: number;
  max: number;
  cor: string;
}) {
  const pct = max > 0 ? Math.round((total / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground-800">
          {rotulo} <span className="text-secondary-500">· {vendas} vendas</span>
        </span>
        <span className="font-bold text-foreground-950">{formatarCentavos(total)}</span>
      </div>
      <div className="mt-1 h-3 overflow-hidden rounded-full bg-secondary-100">
        <div className={`h-full rounded-full ${cor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}